import { Component, OnInit, Input, EventEmitter, Output, ElementRef, ViewChild } from '@angular/core';
import { trigger, transition, style, animate, state } from '@angular/animations';
import { FormBuilder, FormGroup, Validators, FormArray, AbstractControl, ValidatorFn, ValidationErrors } from '@angular/forms';
import { Observable, combineLatest } from 'rxjs';
import * as _ from 'lodash';

import { BaseComponent } from '../../../../_base/base.component';

import { QuestionType, DegreeOfDifficulty } from '../../../../_enums';
import { Question, Curriculum, Objective, QuestionOption, QuestionOptionPossible, CurriculumLevel, ObjectiveLevel } from '../../../../_models';
import { QuestionService, AssignmentService, CurriculumService, ObjectiveService } from '../../../../_services';

import * as ClassicEditor from 'ckeditor5-build-tia-mathtype';
import { CkEditorConfig } from '../../../../_configs';


@Component({
  selector: 'app-question-form',
  templateUrl: './question-form.component.html',
  styleUrls: ['./question-form.component.scss'],
  animations: [
    trigger('delay', [
      transition('* => void', [animate('0ms 500ms ease-out')]),
      transition('void => *', [
        style({ 'height': 0, overflow: 'hidden' }),
        animate('500ms ease-out')]),
    ]),
    trigger('openedClosed',
      [
        state('closed', style({ paddingTop: 0, height: 0, })),
        transition('closed => opened', [animate('500ms ease-out')]),
        transition('opened => closed', [animate('500ms ease-in')]),
      ])
  ]

})
export class QuestionFormComponent extends BaseComponent implements OnInit {

  @Input('questionId') questionId: number;
  @Input('sectionSubjectId') sectionSubjectId: number;
  @Input('assignmentView') assignmentView = false;
  @Input('assignmentId') assignmentId: number;
  @Input('assignmentLive') assignmentLive: number;
  @Input('questionsBankView') questionsBankView = false;
  @Input('questionsBankObjectiveId') questionsBankObjectiveId: number;

  @Output('questionAdded') questionAdded = new EventEmitter<Question>();
  @Output('questionUpdated') questionUpdated = new EventEmitter<Question>();
  @Output('cancel') cancel = new EventEmitter();

  objectives: Objective[];
  objectiveHaveCode = false;
  question: Question;
  saveLoaderActive = false;
  loaderActive = false;


  questionForm: FormGroup;
  QuestionType = QuestionType;
  optionsVisible = false;
  expandedObjective: number[] = [];
  selectedObjectiveId: number;
  lastObjectivesLevelId: number;
  selectedObjectiveView: Objective;
  objectivesListParent: Objective;
  objectivesList: Objective[];

  public Editor = ClassicEditor;
  public CkEditorConfig = CkEditorConfig;


  constructor(
    private questionService: QuestionService,
    private curriculumService: CurriculumService,
    private objectiveService: ObjectiveService,
    private fb: FormBuilder

  ) {
    super();
  }


  get title() {
    return this.questionForm ? this.questionForm.get('title') : null;
  }

  get description() {
    return this.questionForm ? this.questionForm.get('description') : null;
  }


  get questionType() {
    return this.questionForm ? this.questionForm.get('questionType') : null;
  }

  get questionFeedback() {
    return this.questionForm ? this.questionForm.get('questionFeedback') : null;
  }

  get singleFeedback() {
    return this.questionForm ? this.questionForm.get('singleFeedback') : null;
  }

  get correctFeedback() {
    return this.questionForm ? this.questionForm.get('correctFeedback') : null;
  }

  get incorrectFeedback() {
    return this.questionForm ? this.questionForm.get('incorrectFeedback') : null;
  }

  get questionRequired() {
    return this.questionForm ? this.questionForm.get('questionRequired') : null;
  }

  get degreeOfDifficulty() {
    return this.questionForm ? this.questionForm.get('degreeOfDifficulty') : null;
  }

  get solvingTime() {
    return this.questionForm ? this.questionForm.get('solvingTime') : null;
  }

  get controlsDisabled(): boolean {
    if (this.question && this.question.assignmentsIds && this.question.assignmentsIds.length > 1)
      return true;

    if (this.questionsBankView && this.question && this.question.assignmentsIds && this.question.assignmentsIds.length === 1)
      return true;

    if (this.assignmentView && this.assignmentLive)
      return true;

    return false;
  }


  ngOnInit() {
    this.loaderActive = true;
    combineLatest(
      this.questionId ? this.questionService.getByIdWithOptions(this.questionId, this.assignmentId) : Observable.of<Question>(null),
      this.curriculumService.getBySectionSubjectId(this.sectionSubjectId),
      this.curriculumService.getObjectiveLevelsBySectionSubjectId(this.sectionSubjectId),
      this.objectiveService.getTopThatHaveLastChildrenBySectionSubjectId(this.sectionSubjectId),
    ).subscribe(([question, curriculum, objectiveLevels, objectives]) => {

      this.question = question;
      this.objectiveHaveCode = curriculum.objectiveHaveCode;

      const lastObjectivesLevel = _.chain(objectiveLevels).filter(l => !l.child).head().value();
      this.lastObjectivesLevelId = lastObjectivesLevel ? lastObjectivesLevel.objectiveLevelId : null;

      this.objectives = objectives;
      this.objectivesList = objectives;

      if (this.question && this.question.objectiveId) {
        this.selectedObjectiveId = this.question.objectiveId;
        this.initViewObjective();
      } else if (this.questionsBankView) {
        this.selectedObjectiveId = this.questionsBankObjectiveId;
        this.initViewObjective();
      }

      this.initQuestionForm();


      this.loaderActive = false;
    });
  }

  // disableFormControls() {
  //   const disable = (formGroup: FormGroup) => {
  //     (<any>Object).values(formGroup.controls)
  //       .forEach(control => {
  //         control.disable();
  //         if (control.controls) {
  //           disable(control);
  //         }
  //       });
  //   };
  //   disable(this.questionForm);
  // }


  backToParentObjective() {
    if (!this.objectivesListParent.parentId) {
      this.objectivesListParent = null;
      this.objectivesList = this.objectives;
      return;
    }

    this.objectivesListParent = _.chain(this.objectives)
      .filter(o => o.objectiveId === this.objectivesListParent.parentId).head().value();

    if (this.objectivesListParent) {
      this.objectivesList = this.objectivesListParent.objectives;
    }
  }

  selectObjective(objective: Objective) {
    if (objective.objectiveLevelId === this.lastObjectivesLevelId)
      return;
    this.objectivesListParent = objective;
    this.objectivesList = objective.objectives;
  }

  selectedObjectiveChange(objectiveId: number) {
    this.selectedObjectiveId = objectiveId;
    this.initViewObjective();
  }

  initQuestionForm() {
    this.questionForm = this.fb.group({
      title: [{ value: this.question ? this.question.title : '', disabled: this.controlsDisabled }, [Validators.required]],
      description: [{ value: this.question ? this.question.description : '', disabled: this.controlsDisabled }, []],
      questionType: [{ value: this.question ? this.question.questionType : '', disabled: this.controlsDisabled }, [Validators.required]],
      degreeOfDifficulty: [{ value: this.question && this.question.degreeOfDifficulty ? this.question.degreeOfDifficulty : '', disabled: this.controlsDisabled }, []],
      solvingTime: [{ value: this.question && this.question.solvingTime > 0 ? this.question.solvingTime : null, disabled: this.controlsDisabled }, [Validators.min(1)]],
    });

    if (this.assignmentView) {
      const controlsDisabled = this.assignmentLive;
      this.questionForm.addControl('questionFeedback', this.fb.control(
        { value: this.question ? this.question.feedbackType : null, disabled: controlsDisabled }
      ));

      this.questionForm.addControl('singleFeedback', this.fb.control(
        { value: this.question ? this.question.singleFeedback : null, disabled: controlsDisabled }
      ));

      this.questionForm.addControl('correctFeedback', this.fb.control(
        { value: this.question ? this.question.correctFeedback : null, disabled: controlsDisabled }
      ));

      this.questionForm.addControl('incorrectFeedback', this.fb.control(
        { value: this.question ? this.question.incorrectFeedback : null, disabled: controlsDisabled }
      ));

      this.questionForm.addControl('questionRequired', this.fb.control(
        { value: this.question ? this.question.isRequired : false, disabled: controlsDisabled }
      ));
    }

    if (this.question) {
      this.questionTypeChange();
    }

    this.optionsVisible = this.question
      && this.question.degreeOfDifficulty !== null
      && this.question.degreeOfDifficulty !== DegreeOfDifficulty.None;
  }

  ckEditorReady() {
    this.description.setValue(this.question ? this.question.description : '');
  }

  questionTypeChange() {
    this.questionForm.removeControl('multipleChoice');
    this.questionForm.removeControl('checkbox');
    this.questionForm.removeControl('trueFalse');
    this.questionForm.removeControl('fillInBlanks');
    this.questionForm.removeControl('essay');
    this.questionForm.removeControl('matching');
    this.questionForm.removeControl('uploadDocument');

    if (+this.questionType.value === QuestionType.MultipleChoice)
      this.questionForm.addControl('multipleChoice', this.fb.group({}));
    else if (+this.questionType.value === QuestionType.Checkbox)
      this.questionForm.addControl('checkbox', this.fb.group({}));
    else if (+this.questionType.value === QuestionType.TrueFalse)
      this.questionForm.addControl('trueFalse', this.fb.group({}));
    else if (+this.questionType.value === QuestionType.FillInBlanks)
      this.questionForm.addControl('fillInBlanks', this.fb.group({}));
    else if (+this.questionType.value === QuestionType.Essay)
      this.questionForm.addControl('essay', this.fb.group({}));
    else if (+this.questionType.value === QuestionType.Matching)
      this.questionForm.addControl('matching', this.fb.group({}));
    else if (+this.questionType.value === QuestionType.UploadDocument)
      this.questionForm.addControl('uploadDocument', this.fb.group({}));

    if (+this.questionType.value === QuestionType.FillInBlanks) {
      this.description.setValue('');
    }
  }

  questionFeedbackChange() {
    if (+this.questionFeedback.value === 1) {
      this.correctFeedback.setValue(null);
      this.incorrectFeedback.setValue(null);
    } else if (+this.questionFeedback.value === 2) {
      this.singleFeedback.setValue(null);
    } else {
      this.correctFeedback.setValue(null);
      this.incorrectFeedback.setValue(null);
      this.singleFeedback.setValue(null);
    }
  }

  initViewObjective() {
    this.selectedObjectiveView = null;
    if (!this.selectedObjectiveId)
      return;

    const checkObjectives = (objectivesArr: Objective[]): Objective => {
      if (!objectivesArr || objectivesArr.length === 0)
        return null;
      for (let i = objectivesArr.length - 1; i >= 0; i--) {
        const obj = objectivesArr[i];
        if (obj.objectiveId === this.selectedObjectiveId) {
          const selected = _.cloneDeep(obj);
          selected.objectives = [];
          return selected;
        }
        const child = checkObjectives(obj.objectives);
        if (child) {
          const parent = _.cloneDeep(obj);
          parent.objectives = [child];
          return parent;
        }
      }
      return null;
    };

    this.selectedObjectiveView = checkObjectives(this.objectives);
  }


  submitQuestionForm() {
    const markFormAsTouched = (formGroup: FormGroup) => {
      (<any>Object).values(formGroup.controls)
        .forEach(control => {
          if (control.enabled)
            control.markAsTouched();
          if (control.controls) {
            markFormAsTouched(control);
          }
        });
    };
    markFormAsTouched(this.questionForm);
    if (!this.questionForm.valid)
      return;



    const question: Question = {
      questionId: this.question ? this.question.questionId : 0,
      objectiveId: this.selectedObjectiveId,
      title: this.title.value,
      description: this.description.value,
      questionType: + this.questionType.value,
      assignmentId: this.assignmentId ? this.assignmentId : null,
      //////////////
      feedbackType: this.questionFeedback ? +this.questionFeedback.value : null,
      singleFeedback: this.singleFeedback ? this.singleFeedback.value : null,
      correctFeedback: this.correctFeedback ? this.correctFeedback.value : null,
      incorrectFeedback: this.incorrectFeedback ? this.incorrectFeedback.value : null,
      isRequired: this.questionRequired ? this.questionRequired.value : null,
      //////////////
      solvingTime: this.solvingTime ? +this.solvingTime.value : null,
      //////////////
      // order: 0,
      degreeOfDifficulty: this.degreeOfDifficulty.value ? +this.degreeOfDifficulty.value : null,
      assignmentsIds: this.question ? this.question.assignmentsIds : [],
    };

    if (!this.controlsDisabled)
      this.fillQuestionTypeData(question);

    this.saveQuestion(question);
  }

  fillQuestionTypeData(question: Question) {
    if (question.questionType === QuestionType.MultipleChoice ||
      question.questionType === QuestionType.Checkbox) {

      question.options = [];

      let options = null;
      if (question.questionType === QuestionType.MultipleChoice)
        options = this.questionForm.value.multipleChoice.options;
      else if (question.questionType === QuestionType.Checkbox)
        options = this.questionForm.value.checkbox.options;

      _.forEach(options,
        (o) => {
          question.options.push({
            questionOptionId: o.id,
            questionId: question.questionId,
            title: o.title,
            isCorrect: o.isCorrect,
            isCaseSensitive: false,
            possibles: null
          });
        });
    }

    if (question.questionType === QuestionType.Matching) {
      question.options = [];

      _.forEach(this.questionForm.value.matching.options,
        (o) => {
          question.options.push({
            questionOptionId: o.id,
            questionId: question.questionId,
            title: o.title,
            matchTitle: o.matchTitle,
            isCaseSensitive: false,
            possibles: null
          });
        });
    }

    if (question.questionType === QuestionType.FillInBlanks) {
      question.description = this.questionForm.value.fillInBlanks.description;
      question.options = [];
      _.forEach(this.questionForm.value.fillInBlanks.options, (o) => {
        const option: QuestionOption = {
          questionOptionId: o.id,
          questionId: question.questionId,
          title: o.title,
          matchTitle: o.matchTitle,
          isCaseSensitive: o.isCaseSensitive,
          possibles: []
        };
        _.forEach(o.possibles, (p) => {
          const possible: QuestionOptionPossible = {
            questionOptionPossibleId: p.id,
            questionOptionId: option.questionOptionId,
            title: p.title,
            isCaseSensitive: p.isCaseSensitive,
          };
          option.possibles.push(possible);
        });
        question.options.push(option);
      });
    }

    if (question.questionType === QuestionType.TrueFalse) {
      question.trueFalseAnswer = this.questionForm.value.trueFalse.trueFalseAnswer;
    }

    if (question.questionType === QuestionType.Essay) {
      question.maxChars = this.questionForm.value.essay.maxChars;
      question.rows = this.questionForm.value.essay.rows;
    }

    if (question.questionType === QuestionType.UploadDocument) {
      question.fileSize = this.questionForm.value.uploadDocument.fileSize;
      question.fileTypes = this.questionForm.value.uploadDocument.fileTypes;
    }
  }

  saveQuestion(question: Question) {
    if (question.questionId > 0) {
      this.saveLoaderActive = true;
      this.questionService
        .updateQuestion(question)
        .subscribe(q => {
          this.showSuccess('AcademicModule.QuestionUpdatedSuccessfully', 't-60P');
          this.saveLoaderActive = false;
          this.questionUpdated.emit(q);
        });
    } else {
      question.assignmentsIds = this.assignmentId ? [this.assignmentId] : [];
      this.saveLoaderActive = true;
      this.questionService
        .addQuestion(question)
        .subscribe(q => {
          this.showSuccess('AcademicModule.QuestionAddedSuccessfully', 't-60P');
          this.saveLoaderActive = false;
          this.questionAdded.emit(q);
        });
    }
  }

  // getBlanksString() {
  //   let str = '';
  //   if (this.description.value) {
  //     _.forEach((this.description.value as string).split(/(\[[^[\]\r\n]+\])+/), s => {
  //       str += s.startsWith('[') && s.endsWith(']') ? '<em class="bg-yellow">' + s + '</em>' : s;
  //     });
  //   }
  //   return str;
  // }
}
